import { signal, computed } from '@angular/core';
import { Event } from 'api/models';

export interface EventWithOrgSiteKey extends Event {
    read_only: boolean;
    orgSiteKey: string;
}

export interface EventsState {
    events: EventWithOrgSiteKey[];
    loading: boolean;
    error: string | null;
    lastUpdated: number;
    totalCount: number;
}

const initialState: EventsState = {
    events: [],
    loading: false,
    error: null,
    lastUpdated: 0,
    totalCount: 0,
};

export const eventsState = signal<EventsState>(initialState);

// Computed values
export const getEvents = computed(() => eventsState().events);
export const isEventsLoading = computed(() => eventsState().loading);
export const getEventsError = computed(() => eventsState().error);
export const getEventsLastUpdated = computed(() => eventsState().lastUpdated);
export const getEventsCount = computed(() => eventsState().totalCount);

// Computed derived values
export const getFilteredEvents = computed(() => {
    const events = eventsState().events;
    // Could add filtering logic here if needed
    return events;
});

// Data change tracking
export const eventsDataChangeSignal = signal<{
    type: 'add' | 'update' | 'delete' | 'refresh' | 'load';
    data?: EventWithOrgSiteKey;
    timestamp: number;
}>({
    type: 'refresh',
    data: undefined,
    timestamp: Date.now(),
});

// Get data change signal
export const getEventsDataChangeSignal = computed(() =>
    eventsDataChangeSignal()
);

// Trigger signals for data operations
export const triggerEventsDataChange = (
    type: 'add' | 'update' | 'delete' | 'refresh' | 'load',
    data?: EventWithOrgSiteKey
) => {
    eventsDataChangeSignal.set({
        type,
        data,
        timestamp: Date.now(),
    });
};

// Actions
export const setEventsLoading = (loading: boolean) => {
    eventsState.update(state => ({
        ...state,
        loading,
    }));
};

export const setEventsError = (error: string | null) => {
    eventsState.update(state => ({
        ...state,
        error,
    }));
};

export const setEvents = (events: EventWithOrgSiteKey[]) => {
    eventsState.update(state => ({
        ...state,
        events,
        totalCount: events.length,
        lastUpdated: Date.now(),
    }));
    triggerEventsDataChange('load');
};

export const addEvent = (event: EventWithOrgSiteKey) => {
    eventsState.update(state => ({
        ...state,
        events: [...state.events, event],
        totalCount: state.totalCount + 1,
        lastUpdated: Date.now(),
    }));
    triggerEventsDataChange('add', event);
};

export const updateEvent = (updatedEvent: EventWithOrgSiteKey) => {
    eventsState.update(state => ({
        ...state,
        events: state.events.map(event =>
            event.eventKey === updatedEvent.eventKey ? updatedEvent : event
        ),
        lastUpdated: Date.now(),
    }));
    triggerEventsDataChange('update', updatedEvent);
};

export const removeEvent = (eventKey: string) => {
    const eventToRemove = eventsState().events.find(
        event => event.eventKey === eventKey
    );
    eventsState.update(state => ({
        ...state,
        events: state.events.filter(event => event.eventKey !== eventKey),
        totalCount: Math.max(0, state.totalCount - 1),
        lastUpdated: Date.now(),
    }));
    if (eventToRemove) {
        triggerEventsDataChange('delete', eventToRemove);
    }
};

export const clearEvents = () => {
    eventsState.update(state => ({
        ...state,
        events: [],
        totalCount: 0,
        lastUpdated: Date.now(),
    }));
    triggerEventsDataChange('refresh');
};

// Enhanced refresh function
export const refreshEvents = () => {
    triggerEventsDataChange('refresh');
};

export const bulkAddEvents = (newEvents: EventWithOrgSiteKey[]) => {
    eventsState.update(state => ({
        ...state,
        // Prepend new events so newest appear at the top
        events: [...newEvents, ...state.events],
        totalCount: state.totalCount + newEvents.length,
        lastUpdated: Date.now(),
    }));
    triggerEventsDataChange('refresh');
};
